import { arrayOf, objectOf } from "@altostra/type-validations";

import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES } from "@lib/shared_constants";

import type { DecisioningStudioLaunchValidationError } from "../decisioningStudioAgentsAppGroupIdDsaIdLaunchValidation/types";
import { isDecisioningStudioLaunchValidationError } from "../decisioningStudioAgentsAppGroupIdDsaIdLaunchValidation/validators";

export interface PostDecisioningStudioAgentsAppGroupIdDsaIdLaunchProps {
  /** The unique identifier of the app group */
  appGroupId: string;
  /** The unique identifier of the decisioning studio agent */
  dsaId: string;
}

export interface PostDecisioningStudioAgentsAppGroupIdDsaIdLaunchResult {
  success: boolean;
}

/**
 * The launch endpoint re-runs LaunchReadinessValidator as its first gate and rejects with the
 * same error list the GET launch_validation path returns, under `meta.errors`.
 */
export type PostDecisioningStudioAgentsAppGroupIdDsaIdLaunchIncompleteError = FetchUserEncodedError<
  SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["LAUNCH_INCOMPLETE"],
  { errors: DecisioningStudioLaunchValidationError[] }
>;

export type PostDecisioningStudioAgentsAppGroupIdDsaIdLaunchError =
  PostDecisioningStudioAgentsAppGroupIdDsaIdLaunchIncompleteError;

const isLaunchIncompleteMeta = objectOf<{ errors: DecisioningStudioLaunchValidationError[] }>({
  errors: arrayOf(isDecisioningStudioLaunchValidationError),
});

/**
 * Narrows a launch failure to the readiness gate rejection.
 *
 * Keyed off `error_code`, never HTTP status: the controller returns 422 while the Storybook
 * fakes reject with 500 carrying the same body. The payload is checked with the validation
 * endpoint's own validator, so the two paths accept exactly the same errors; a malformed body
 * returns false and the caller falls back to generic copy.
 */
export const isPostDecisioningStudioAgentsAppGroupIdDsaIdLaunchIncompleteError = (
  error: FetchRequestError
): error is PostDecisioningStudioAgentsAppGroupIdDsaIdLaunchIncompleteError => {
  if (!(error instanceof FetchUserEncodedError) || error.errorCode !== "LAUNCH_INCOMPLETE") {
    return false;
  }

  return isLaunchIncompleteMeta(error.meta);
};
